import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const FreelancerSubscriptionInfoPage = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        navigate("/employee-login");
        return;
      }
      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-subscription-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200) {
          const sortedData = [...response.data].sort((a, b) => {
            const idA = parseInt(a.subscription_id, 10);
            const idB = parseInt(b.subscription_id, 10);
            return idA - idB;
          });
          setSubscriptionInfo(sortedData);
          setFilteredData(sortedData);
        } else {
          setError('Unexpected status code');
        }
      } catch (err) {
        setError('Failed to fetch subscription info.');
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionInfo();
  }, [apiBaseURL, navigate]);

  useEffect(() => {
    let filtered = subscriptionInfo;

    if (searchQuery) {
      filtered = filtered.filter(info =>
        info.freelancer_id?.toString().includes(searchQuery) ||
        info.freelancer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [searchQuery, subscriptionInfo]);

  const orderedColumns = [
    "subscription_id",
    "freelancer_id",
    "freelancer_name",
    "subscribed_at_time",
    "subscription_plan_name",
    "subscription_validity_from",
    "subscription_validity_to",
    "payment_date",
    "subscription_fees_before_gst",
    "subscription_gst_amount",
    "subscription_fees_with_gst",
    "subscription_fees_payment_status",
    "invoice_number",
    "razorpay_payment_id",
    "razorpay_order_id",
    "razorpay_fee",
    "razorpay_fee_gst"
  ];

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, index) => (
              <th key={index} className="px-4 py-2 text-left">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500">No data available</td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b">
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="px-4 py-2 whitespace-nowrap">
                    {(() => {
                      if (col.toLowerCase().includes('validity_from') || col.toLowerCase().includes('validity_to')) {
                        return row[col]
                          ? new Date(row[col]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—';
                      } else if (col.toLowerCase().includes('date') || col.toLowerCase().includes('time')) {
                        return row[col]
                          ? new Date(row[col]).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                          : '—';
                      } else if (col.toLowerCase().includes('razorpay_fee')) {
                        return row[col] !== null && row[col] !== undefined ? `₹${Number(row[col]).toFixed(2)}` : '₹0.00';
                      } else {
                        return row[col] !== null && row[col] !== undefined ? row[col].toString() : '—';
                      }
                    })()}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const downloadExcel = () => {
    const excelData = filteredData.map(row => {
      const newRow = {};
      orderedColumns.forEach(col => {
        newRow[col] = row[col];
      });
      return newRow;
    });

    const now = new Date();
    const formattedDateTime = now.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    const headerRows = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Subscription Info'],
      [`Downloaded on: ${formattedDateTime}`],
      [],
      orderedColumns
    ];

    const dataRows = excelData.map(row => orderedColumns.map(col => row[col]));
    const combinedRows = [...headerRows, ...dataRows];

    const worksheet = XLSX.utils.aoa_to_sheet(combinedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscription Info');

    const fileName = `Freelancer_Subscription_Info_${now.toISOString().replace(/:/g, '-').replace('T', '_').split('.')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  if (loading) return <div>Loading subscription info...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-64">
      <h2 className="text-2xl font-semibold mb-4">Freelancer Subscription Info (SUBSCRIBER'S CURRENT PLAN)</h2>

      <div className="mb-4 flex flex-wrap gap-4">
        <input
          type="text"
          className="border px-3 py-2 rounded"
          placeholder="Search Freelancer ID or Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="mb-6">
        <div className="bg-blue-100 border border-blue-400 text-blue-800 text-xl font-semibold px-6 py-3 rounded shadow-sm">
          📊 Total Subscribers: {filteredData.length}
        </div>
      </div>

      <div className="mb-4">
        <button
          onClick={downloadExcel}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow"
        >
          📥 Download as Excel
        </button>
      </div>

      <Table columns={orderedColumns} data={filteredData} />
    </div>
  );
};

export default FreelancerSubscriptionInfoPage;
